import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #highResImagesLoading = /** @type {Set<string>} */ (new Set());

  #highResPreloaders = /** @type {Map<string, HTMLImageElement>} */ (new Map());

  #originalImageSources = /** @type {Map<HTMLImageElement, Record<string, string | null>>} */ (new Map());

  /** @type {HTMLElement | null} */
  #mediaScroller = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.#mediaScroller = this.querySelector('.dialog-zoomed-gallery');
    this.#mediaScroller?.addEventListener('scroll', this.handleScroll, { passive: true });
    this.addEventListener('rx:zoom-change', this.#handleZoomChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.#mediaScroller?.removeEventListener('scroll', this.handleScroll);
    this.#mediaScroller = null;
    this.removeEventListener('rx:zoom-change', this.#handleZoomChange);
    this.#restoreHighResolutionImages();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (prefersReducedMotion() || !supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    // A compact touch viewport starts at 1x. Do not spend memory on a zoom
    // source until the visitor explicitly pinches or double taps.
    if (isCompactTouchViewport() && !mediaContainer.querySelector('drag-zoom-wrapper[data-zoomed]')) return false;

    const highResolutionUrl = getAdaptiveHighResolutionUrl(image);
    if (
      !highResolutionUrl ||
      this.#highResImagesLoaded.has(highResolutionUrl) ||
      this.#highResImagesLoading.has(highResolutionUrl)
    ) {
      return false;
    }

    this.#highResImagesLoading.add(highResolutionUrl);

    const preloader = new Image();
    preloader.decoding = 'async';
    this.#highResPreloaders.set(highResolutionUrl, preloader);
    preloader.onload = () => {
      this.#highResImagesLoading.delete(highResolutionUrl);
      this.#highResPreloaders.delete(highResolutionUrl);
      if (!this.refs.dialog.open || !image.isConnected) return;

      if (!this.#originalImageSources.has(image)) {
        this.#originalImageSources.set(image, {
          src: image.getAttribute('src'),
          srcset: image.getAttribute('srcset'),
          sizes: image.getAttribute('sizes'),
          loading: image.getAttribute('loading'),
        });
      }

      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.setAttribute('src', highResolutionUrl);
      image.setAttribute('data-rx-zoom-high-res', 'true');
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    preloader.onerror = () => {
      this.#highResImagesLoading.delete(highResolutionUrl);
      this.#highResPreloaders.delete(highResolutionUrl);
    };
    preloader.src = highResolutionUrl;

    return true;
  }

  #handleZoomChange = (event) => {
    if (!(event instanceof CustomEvent) || !event.detail?.isZoomed || !(event.target instanceof Element)) return;

    const mediaContainer = event.target.closest('.product-media-container');
    if (!(mediaContainer instanceof HTMLElement)) return;

    // Keep at most one decoded high-resolution bitmap active on compact touch
    // devices, even if the visitor zooms several slides in one dialog session.
    if (isCompactTouchViewport()) this.#restoreHighResolutionImages();
    this.loadHighResolutionImage(mediaContainer);
  };

  #restoreHighResolutionImages() {
    for (const preloader of this.#highResPreloaders.values()) {
      preloader.onload = null;
      preloader.onerror = null;
      preloader.removeAttribute('src');
    }

    for (const [image, attributes] of this.#originalImageSources) {
      if (!image.isConnected) continue;

      restoreAttribute(image, 'sizes', attributes.sizes);
      restoreAttribute(image, 'srcset', attributes.srcset);
      restoreAttribute(image, 'src', attributes.src);
      restoreAttribute(image, 'loading', attributes.loading);
      image.removeAttribute('data-rx-zoom-high-res');
    }

    this.#originalImageSources.clear();
    this.#highResImagesLoaded.clear();
    this.#highResImagesLoading.clear();
    this.#highResPreloaders.clear();
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      if (i === activeIndex) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (prefersReducedMotion() || !supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    this.#restoreHighResolutionImages();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      if (i === index) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

/**
 * Select a zoom source that is sharp on compact high-DPR screens without
 * decoding a 3840px bitmap for every mobile slide.
 * @param {HTMLImageElement} image
 * @returns {string | null}
 */
function getAdaptiveHighResolutionUrl(image) {
  const maximumUrl = image.getAttribute('data_max_resolution');
  if (!isCompactTouchViewport()) return maximumUrl;

  const cappedDpr = Math.min(window.devicePixelRatio || 1, 2);
  const requiredPixels = Math.ceil(Math.max(window.innerWidth, window.innerHeight) * cappedDpr);

  if (requiredPixels <= 1600) {
    return image.getAttribute('data_zoom_1600') || image.getAttribute('data_zoom_2048') || maximumUrl;
  }

  return image.getAttribute('data_zoom_2048') || image.getAttribute('data_zoom_1600') || maximumUrl;
}

/**
 * Match the mobile, touch-tablet portrait, and short touch-landscape layouts.
 * @returns {boolean}
 */
function isCompactTouchViewport() {
  return (
    window.innerWidth <= 749 ||
    window.matchMedia(
      '(min-width: 750px) and (max-width: 989px) and (hover: none) and (pointer: coarse), ' +
        '(min-width: 568px) and (max-width: 1024px) and (max-height: 500px) and (orientation: landscape) and (hover: none) and (pointer: coarse)'
    ).matches
  );
}

/**
 * Restore an attribute exactly as it existed before zoom.
 * @param {Element} element
 * @param {string} name
 * @param {string | null} value
 */
function restoreAttribute(element, name, value) {
  if (value === null) {
    element.removeAttribute(name);
  } else {
    element.setAttribute(name, value);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
